import type { AxiosInstance } from 'axios';

import type { PaymentSessionMode } from '@subscriptions/domain/types/SubscriptionsConst';
import type { PaymentMethodTypeType } from '@subscriptions/domain/types/SubscriptionsTypeSafe';
import { PAYMENT_METHOD_ENDPOINTS } from './endpoints';
import type {
  ApiEnvelope,
  ConfirmSessionApiRequest,
  ConfirmSessionApiResponse,
  GenerateSessionApiRequest,
  GenerateSessionApiResponse,
  ListPaymentMethodsApiResponse,
} from './types';

/** HTTP client type for payment method operations */
export type PaymentMethodHttpClientType = {
  listPaymentMethods: (type: PaymentMethodTypeType) => Promise<ListPaymentMethodsApiResponse>;
  generateSession: (
    blockUuid: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ) => Promise<GenerateSessionApiResponse>;
  confirmSession: (
    blockUuid: string,
    sessionId: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ) => Promise<ConfirmSessionApiResponse>;
  forcePayment: (blockUuid: string) => Promise<void>;
};

/**
 * Creates the HTTP client for payment method API operations.
 * All routes target parrot-subscriptions-api (Stripe session routes under the
 * subscription-block resource plus the org-level `v1/payment-methods` listing)
 * and unwrap the standard `{data, datetime}` envelope.
 *
 * @param props.httpClientSubscription - Axios instance bound to parrot-subscriptions-api
 */
export const createPaymentMethodHttpClient = (props: {
  httpClientSubscription: AxiosInstance;
}): PaymentMethodHttpClientType => {
  const { httpClientSubscription } = props;

  const listPaymentMethods = async (type: PaymentMethodTypeType): Promise<ListPaymentMethodsApiResponse> => {
    const response = await httpClientSubscription.get<ApiEnvelope<ListPaymentMethodsApiResponse>>(
      PAYMENT_METHOD_ENDPOINTS.LIST,
      { params: { type } },
    );
    return response.data.data;
  };

  const generateSession = async (
    blockUuid: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ): Promise<GenerateSessionApiResponse> => {
    const body: GenerateSessionApiRequest = { isUpdate };
    const response = await httpClientSubscription.post<ApiEnvelope<GenerateSessionApiResponse>>(
      `${PAYMENT_METHOD_ENDPOINTS.GENERATE_SESSION(blockUuid)}?mode=${mode}`,
      body,
    );
    return response.data.data;
  };

  const confirmSession = async (
    blockUuid: string,
    sessionId: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ): Promise<ConfirmSessionApiResponse> => {
    const body: ConfirmSessionApiRequest = { sessionId, mode, isUpdate };
    const response = await httpClientSubscription.post<ApiEnvelope<ConfirmSessionApiResponse>>(
      PAYMENT_METHOD_ENDPOINTS.CONFIRM_SESSION(blockUuid),
      body,
    );
    return response.data.data;
  };

  const forcePayment = async (blockUuid: string): Promise<void> => {
    await httpClientSubscription.post(PAYMENT_METHOD_ENDPOINTS.FORCE_PAYMENT(blockUuid));
  };

  return { listPaymentMethods, generateSession, confirmSession, forcePayment };
};
